import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { documentsAPI, propertiesAPI, apartmentsAPI, filesAPI, fileViewUrl } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { FileText, FileBadge, FileImage, Upload, Trash2, Search, Download, RefreshCw, Plus } from 'lucide-react';
import { fmt, errMsg } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const CATEGORIES = ['safety', 'rules', 'maintenance', 'legal', 'general'];

function fileIcon(mimeType) {
  if (!mimeType) return <FileText size={28} color="var(--brand)" />;
  if (mimeType.includes('pdf'))   return <FileBadge size={28} color="var(--rose)" />;
  if (mimeType.includes('image')) return <FileImage size={28} color="var(--emerald)" />;
  return <FileText size={28} color="var(--brand)" />;
}

function fmtBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const EMPTY_FORM = { title: '', description: '', category: 'general', propertyId: '', apartmentId: '' };

export default function Documents() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { can }   = usePermissions();
  const isStaff   = user?.role !== 'tenant';
  const canEdit   = user?.role === 'admin' || user?.role === 'manager'; // for filter/data-load behavior
  const canCreate = can('documents', 'create');
  const canDelete = can('documents', 'delete');

  const [docs, setDocs]         = useState([]);
  const [docUrls, setDocUrls]   = useState({});
  const [properties, setProps]  = useState([]);
  const [allApts,   setAllApts] = useState([]);
  const [filterApts, setFilterApts] = useState([]);
  const [formApts,  setFormApts]= useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [catF, setCatF]         = useState('');
  const [propF, setPropF]       = useState('');
  const [aptF,  setAptF]        = useState('');
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [file, setFile]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (catF)   q.category    = catF;
    if (search) q.search      = search;
    if (propF)  q.propertyId  = propF;
    if (aptF)   q.apartmentId = aptF;
    const empty = Promise.resolve({ data: { data: [] } });
    const calls = canEdit
      ? [documentsAPI.getAll(q), propertiesAPI.getAll(), apartmentsAPI.getAll()]
      : [documentsAPI.getAll(q), empty, empty];
    Promise.all(calls)
      .then(([d, p, a]) => {
        const list = d.data.data;
        setDocs(list);
        setProps(p.data.data);
        setAllApts(a.data.data);
        // Pre-fetch signed URLs so download links use direct S3 URLs (no async on click)
        Promise.all(
          list.map(async (doc) => {
            const key = doc.fileUrl;
            if (!key || key.startsWith('/')) return [doc._id, key];
            try {
              const res = await filesAPI.getSignedUrl(key);
              return [doc._id, res.data.url];
            } catch {
              return [doc._id, fileViewUrl(key)];
            }
          })
        ).then(entries => setDocUrls(Object.fromEntries(entries)));
      })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  }, [catF, search, propF, aptF, canEdit, t]);

  useEffect(() => { const timer = setTimeout(load, 300); return () => clearTimeout(timer); }, [load]);

  // Update filter apartments when property filter changes
  useEffect(() => {
    if (!propF) { setFilterApts([]); setAptF(''); return; }
    setFilterApts(allApts.filter(a => {
      const pid = (a.propertyId?._id || a.propertyId)?.toString();
      return pid === propF;
    }));
    setAptF('');
  }, [propF, allApts]);

  // Update form apartments when form property changes
  useEffect(() => {
    if (!form.propertyId) { setFormApts([]); setForm(f => ({ ...f, apartmentId: '' })); return; }
    setFormApts(allApts.filter(a => {
      const pid = (a.propertyId?._id || a.propertyId)?.toString();
      return pid === form.propertyId;
    }));
    setForm(f => ({ ...f, apartmentId: '' }));
  }, [form.propertyId, allApts]);

  const openModal = () => { setForm(EMPTY_FORM); setFile(null); setModal(true); };

  const handleUpload = async () => {
    if (!form.title) return toast.warning(t('documentTitleRequired'));
    if (!file)       return toast.warning(t('fileRequired'));
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', form.title);
      if (form.description)  fd.append('description', form.description);
      fd.append('category', form.category);
      if (form.propertyId)   fd.append('propertyId', form.propertyId);
      if (form.apartmentId)  fd.append('apartmentId', form.apartmentId);
      await documentsAPI.upload(fd);
      toast.success(t('documentUploaded'));
      setModal(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (doc) => {
    try {
      await documentsAPI.delete(doc._id);
      toast.success(t('documentDeleted'));
      setConfirmDel(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const catColor = { safety:'var(--rose)', rules:'var(--amber)', maintenance:'var(--brand)', legal:'var(--emerald)', general:'var(--ink-40)' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('documents')}</h1>
          <p className="page-subtitle">{t('buildingDocumentsSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16} /></button>
          {canCreate && (
            <button className="btn btn-primary" onClick={openModal}><Plus size={16} />{t('uploadDocument')}</button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <Search size={15} color="var(--ink-20)" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchDocuments')} />
        </div>
        <select className="form-input form-select" style={{ width: 160 }} value={catF} onChange={e => setCatF(e.target.value)}>
          <option value="">{t('allCategories')}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{t(c)}</option>)}
        </select>
        {canEdit && (
          <select className="form-input form-select" style={{ width: 160 }} value={propF} onChange={e => setPropF(e.target.value)}>
            <option value="">{t('allProperties')}</option>
            {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        )}
        {canEdit && propF && (
          <select className="form-input form-select" style={{ width: 140 }} value={aptF} onChange={e => setAptF(e.target.value)}>
            <option value="">{t('allUnits')}</option>
            {filterApts.map(a => <option key={a._id} value={a._id}>{t('unit')} {a.unit}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : docs.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--ink-40)' }}>
          <FileText size={40} style={{ margin: '0 auto 12px', opacity: .3 }} />
          <div>{t('noDocumentsFound')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {docs.map(doc => (
            <div key={doc._id} className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Header */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {fileIcon(doc.mimeType)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, wordBreak: 'break-word' }}>{doc.title}</div>
                  <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span className="badge" style={{ background: catColor[doc.category] + '18', color: catColor[doc.category], border: `1px solid ${catColor[doc.category]}30`, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      {t(doc.category)}
                    </span>
                    {doc.apartmentId && (
                      <span className="badge" style={{ background: 'var(--brand)18', color: 'var(--brand)', border: '1px solid var(--brand)30', fontSize: 10, fontWeight: 700 }}>
                        {t('unit')} {doc.apartmentId.unit}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {doc.description && (
                <p style={{ fontSize: 13, color: 'var(--ink-60)', lineHeight: 1.6, margin: 0 }}>{doc.description}</p>
              )}

              {/* Meta */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  [t('uploadedBy'), doc.uploadedBy?.name || '—'],
                  [t('fileSize'),   fmtBytes(doc.fileSize)],
                  [t('uploaded'),   fmt.date(doc.createdAt)],
                  [t('property'),   doc.propertyId?.name || t('allProperties')],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: 'var(--ink-40)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* File name */}
              <div style={{ fontSize: 11, color: 'var(--ink-40)', background: 'var(--surface-2)', borderRadius: 6, padding: '6px 10px', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doc.fileName}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                <a
                  className="btn btn-primary btn-sm"
                  style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                  href={docUrls[doc._id] || fileViewUrl(doc.fileUrl) || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download size={13} />{t('download')}
                </a>
                {canDelete && (
                  <button className="btn btn-danger btn-sm btn-icon" onClick={() => setConfirmDel(doc)} title={t('deleteDocument')}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {canCreate && (
        <Modal open={modal} onClose={() => setModal(false)} title={t('uploadDocument')} size="md"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={saving}>
                {saving ? t('saving') : t('upload')}
              </button>
            </>
          }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">{t('documentTitle')} *</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('documentTitlePlaceholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('description')}</label>
              <textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('optional')} />
            </div>
            <div className="form-grid form-grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('category')}</label>
                <select className="form-input form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{t(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('property')}</label>
                <select className="form-input form-select" value={form.propertyId} onChange={e => setForm(f => ({ ...f, propertyId: e.target.value }))}>
                  <option value="">{t('allProperties')}</option>
                  {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            {form.propertyId && (
              <div className="form-group">
                <label className="form-label">{t('unit')}</label>
                <select className="form-input form-select" value={form.apartmentId} onChange={e => setForm(f => ({ ...f, apartmentId: e.target.value }))}>
                  <option value="">{t('allUnits')}</option>
                  {formApts.map(a => <option key={a._id} value={a._id}>{t('unit')} {a.unit}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{t('file')} *</label>
              <div
                style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: file ? 'var(--surface-2)' : 'transparent', transition: 'background .15s' }}
                onClick={() => fileRef.current?.click()}
              >
                <input ref={fileRef} type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                  onChange={e => setFile(e.target.files[0] || null)} />
                {file ? (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{file.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 4 }}>{fmtBytes(file.size)}</div>
                  </div>
                ) : (
                  <div>
                    <Upload size={24} color="var(--ink-20)" style={{ margin: '0 auto 8px' }} />
                    <div style={{ fontSize: 14, color: 'var(--ink-40)' }}>{t('clickToUpload')}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-20)', marginTop: 4 }}>PDF, Word, TXT, Images — max 20 MB</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title={t('deleteDocument')} size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setConfirmDel(null)}>{t('cancel')}</button>
            <button className="btn btn-danger" onClick={() => handleDelete(confirmDel)}>{t('delete')}</button>
          </>
        }>
        {confirmDel && (
          <p style={{ fontSize: 14, color: 'var(--ink-60)' }}>
            {t('removeDocumentConfirm')} <strong>{confirmDel.title}</strong>?
          </p>
        )}
      </Modal>
    </div>
  );
}
